import { cn } from '@/lib/utils';
import { EditableTitle } from '@/components/EditableTitle';
import { EditableDescription } from '@/components/EditableDescription';

export interface PageHeaderLabelProps {
  /** Page/section title (required) */
  title: string;
  /** Icon element - can be plain icon or badged component */
  icon?: React.ReactNode;
  /** Badge element displayed after the title (e.g., AI session badge) */
  badge?: React.ReactNode;
  /** Subtitle text (optional) */
  subtitle?: string | null;
  /** Handler for title changes - enables editing when provided with titleEditable */
  onTitleChange?: (newTitle: string) => Promise<void>;
  /** Handler for subtitle changes - enables editing when provided with subtitleEditable */
  onSubtitleChange?: (newSubtitle: string | null) => Promise<void>;
  /** Whether title is editable (default: false) */
  titleEditable?: boolean;
  /** Whether subtitle is editable (default: false) */
  subtitleEditable?: boolean;
  /** Placeholder text when subtitle is empty and editable */
  subtitlePlaceholder?: string;
  /** Additional CSS classes for the container */
  className?: string;
}

/**
 * PageHeaderLabel - Icon, title, and subtitle section
 *
 * Supports optional editing for title and subtitle.
 * Icon can be any React node (plain icon, badged icon, tooltip trigger, etc.)
 *
 * @example Basic usage
 * <PageHeaderLabel
 *   title="Collections"
 *   icon={<CollectionIcon size={32} />}
 * />
 *
 * @example With editable title
 * <PageHeaderLabel
 *   title={session.title}
 *   onTitleChange={handleTitleChange}
 *   titleEditable={isOwner}
 *   icon={<ContributorsTooltip sessionId={session.globalId} />}
 *   subtitle={session.description}
 *   onSubtitleChange={handleDescriptionChange}
 *   subtitleEditable={isOwner}
 * />
 */
export function PageHeaderLabel({
  title,
  icon,
  badge,
  subtitle,
  onTitleChange,
  onSubtitleChange,
  titleEditable = false,
  subtitleEditable = false,
  subtitlePlaceholder = 'Add a description...',
  className,
}: PageHeaderLabelProps) {
  const canEditTitle = titleEditable && onTitleChange;
  const canEditSubtitle = subtitleEditable && onSubtitleChange;

  return (
    <div className={cn('flex-1 flex items-center gap-4 mr-4', className)}>
      {icon && <div className="flex-shrink-0">{icon}</div>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {canEditTitle ? (
            <EditableTitle
              value={title}
              onChange={onTitleChange}
              disabled={false}
              className="text-2xl font-bold"
            />
          ) : (
            <h1 className="text-2xl font-bold truncate">{title}</h1>
          )}
          {badge}
        </div>
        {(subtitle !== undefined || canEditSubtitle) && (
          canEditSubtitle ? (
            <EditableDescription
              value={subtitle ?? null}
              onChange={onSubtitleChange}
              disabled={false}
              placeholder={subtitlePlaceholder}
              className="text-md text-muted"
            />
          ) : subtitle ? (
            <p className="text-md text-muted truncate">{subtitle}</p>
          ) : null
        )}
      </div>
    </div>
  );
}
